import type { AttachmentPayload, UploadedAttachmentResult } from '@/types/attachment';

const MAX_VIDEO_DURATION_SECONDS = 180;

export function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = size / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function getAttachmentKind(file: File): 'image' | 'video' | 'audio' | 'file' {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();

  if (type.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif)$/i.test(name)) {
    return 'image';
  }

  if (type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(name)) {
    return 'video';
  }

  if (type.startsWith('audio/') || /\.(mp3|wav|ogg|oga|m4a|aac|flac)$/i.test(name)) {
    return 'audio';
  }

  return 'file';
}

export function isVideoTooLong(duration: number | undefined): boolean {
  return typeof duration === 'number' && duration > MAX_VIDEO_DURATION_SECONDS;
}

export function buildAttachmentPayload(result: UploadedAttachmentResult, kind: 'image' | 'video' | 'audio' | 'file', duration?: number): AttachmentPayload {
  const payload: AttachmentPayload = {
    type: kind,
    url: result.url,
    filename: result.filename,
    size: result.size,
  };

  if (kind === 'video' && typeof duration === 'number') {
    payload.duration = Math.round(duration);
  }

  return payload;
}

export function parseAttachmentPayload(content: string): AttachmentPayload | null {
  try {
    const parsed = JSON.parse(content) as Partial<AttachmentPayload>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.type !== 'image' && parsed.type !== 'video' && parsed.type !== 'audio' && parsed.type !== 'file') return null;
    if (typeof parsed.url !== 'string' || typeof parsed.filename !== 'string' || typeof parsed.size !== 'number') return null;
    return parsed as AttachmentPayload;
  } catch {
    return null;
  }
}

export function uploadFiles(files: File[], onProgress?: (percent: number) => void): Promise<UploadedAttachmentResult[]> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    xhr.open('POST', '/api/uploads');
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(xhr.responseText || 'Upload failed'));
        return;
      }

      try {
        const parsed = JSON.parse(xhr.responseText) as { files?: UploadedAttachmentResult[] };
        resolve(parsed.files ?? []);
      } catch {
        reject(new Error('Invalid upload response'));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(formData);
  });
}

export { MAX_VIDEO_DURATION_SECONDS };
